import { useEffect } from "react";
import { useStoreSettings } from "@/hooks/useStoreSettings";

interface SEOOptions {
  title?: string;
  description?: string;
  keywords?: string;
  noIndex?: boolean;
  ogImage?: string;
  canonical?: string;
}

function setMeta(name: string, content: string, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(name: string, attr = "name") {
  const el = document.querySelector(`meta[${attr}="${name}"]`);
  if (el) el.remove();
}

/**
 * Dynamic SEO hook that reads store-level SEO from settings_data
 * and allows per-page overrides. Each store has its own isolated SEO.
 */
export function useSEO(options: SEOOptions = {}) {
  const { settings, seo } = useStoreSettings();

  useEffect(() => {
    // Title: page override > store SEO > store name
    const title = options.title || seo.seo_titulo || settings.store_name || "Loja";
    document.title = title;

    // Meta description
    const desc = options.description || seo.seo_meta_descricao || "";
    if (desc) {
      setMeta("description", desc);
    }

    // Keywords
    const kw = options.keywords || seo.seo_palavras_chave || "";
    if (kw) {
      setMeta("keywords", kw);
    } else {
      removeMeta("keywords");
    }

    // Robots / indexação
    const noIndex = options.noIndex ?? !(seo.seo_indexacao ?? true);
    setMeta("robots", noIndex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    setMeta("og:title", title, "property");
    if (desc) setMeta("og:description", desc, "property");
    setMeta("og:type", "website", "property");
    if (options.ogImage || settings.logo_url) {
      setMeta("og:image", options.ogImage || settings.logo_url || "", "property");
    }

    // Twitter card
    setMeta("twitter:card", "summary_large_image", "name");
    setMeta("twitter:title", title, "name");
    if (desc) setMeta("twitter:description", desc, "name");

    // Canonical
    if (options.canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", options.canonical);
    }

    // Cleanup: restore defaults on unmount
    return () => {
      document.title = seo.seo_titulo || settings.store_name || "Loja";
    };
  }, [
    options.title, options.description, options.keywords, options.noIndex,
    options.ogImage, options.canonical,
    seo.seo_titulo, seo.seo_meta_descricao, seo.seo_palavras_chave, seo.seo_indexacao,
    settings.store_name, settings.logo_url,
  ]);
}
